import { useSyncExternalStore } from 'react';
import type { CalendarEventDto, EventReservationDto } from '../../api/calendar';
import { tCalendar, getCalendarLang, subscribeCalendarLang } from '../../lib/calendar-i18n';
import { Badge } from '../../components/shared';
import { AlertTriangle } from '../../icons';
import './ReservationBadge.css';

/**
 * 회의실 승인 배지 — 일정명 **앞**에 붙는다.
 *
 * 목업(AXI 캘린더 · `.apv-tag`)의 자리와 뜻을 그대로 옮겼다: 승인을 기다리는 동안에는
 * 그 회의가 아직 확정이 아니라는 것을 목록에서 바로 알아야 한다. 확정된 방은 배지를
 * 달지 않는다 — 회의 대부분이 확정 상태라 모든 줄에 표식이 붙으면 정작 봐야 할
 * "대기중"이 묻힌다(목업 주석의 판단을 그대로 따랐다).
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * ⚠ 정본(feature/dev-kyeongeun)에서 «글자 그대로» 옮겼다. 더한 것은 이 머리말뿐이고,
 *   정본 머리말이 달고 있던 「migration 109」 라는 번호만 뺐다(옛 가지 번호다 — 우리 쪽은
 *   2026-09-30 의 마이그 215·216 이다).
 *
 * ⚠ **`status` 를 여기서 «다시» 거른다** — 서버가 이미 취소·반려를 빼고 주는데도 그렇다.
 *   까닭: 이 함수가 읽는 것은 「approved 냐 pending 이냐」이고, 그 둘은 «서버가 둘 다» 준다.
 *   즉 여기 필터는 서버 필터의 되풀이가 아니라 «다른 판정»이다.
 *
 * ⭐ 겹침 정리 (2026-10-01 사용자 결정 · 갈래 ⓒ) — `hasChipTooltip` 이 그것이다.
 *   까닭: 월간 칩·주간 시간 블록에는 스타일 툴팁이 붙어 있는데(PR #1868) 이 배지는 그 칩 «안»에
 *   있고 제 `title` 을 가져, 배지 위에 올리면 «둘 다» 보였다.
 *   ⇒ 그 두 곳만 `hasChipTooltip` 으로 `title` 을 «빼고», 방 이름은 «칩 툴팁이» 말한다.
 *   ⛔ 나머지 셋(일간 표·다가올 일정·선택일 레일)에는 칩 툴팁이 «없어서» 이 `title` 이
 *     «방 이름을 보는 유일한 길»이다 — 거기서는 그대로 둔다.
 *   ⇒ 그래서 이 prop 은 배지의 «취향»이 아니라 부르는 쪽의 «사실»을 말한다. 이름이 그 꼴인 까닭이다.
 * ──────────────────────────────────────────────────────────────────────────────
 */

/** 아직 승인을 기다리는 예약들. 없으면 빈 배열. */
export function pendingReservations(e: Pick<CalendarEventDto, 'reservations'>): EventReservationDto[] {
  return (e.reservations ?? []).filter((r) => r.status === 'pending');
}

/**
 * 승인을 기다리는 방 이름을 사람이 읽는 한 줄로. 없으면 빈 글자.
 * ⭐ 배지와 칩 툴팁이 «같은 글자»를 쓰게 하려고 여기 한 벌만 둔다 —
 *   두 벌이 되면 한쪽만 고쳐지는 날이 온다.
 * ⚠ 이름이 빈 예약이 섞여도 «쉼표만» 남지 않게 거른다.
 */
export function pendingResourceNames(e: Pick<CalendarEventDto, 'reservations'>): string {
  return pendingReservations(e).map((r) => r.resourceName).filter(Boolean).join(', ');
}

/**
 * variant(2026-09-09 사용자 지시): 칩·블록·표처럼 좁은 곳은 기본 'icon', 상세 모달 제목처럼 자리가 있는 곳만 'text'.
 *
 * ⭐ 2026-10-27 — 글자 배지는 공용 `Badge`(warning)가 그린다(사용자 승인 · 전후 화면 확인).
 *   ⚠ 'icon' 은 «종전 그대로» 경고 삼각형이다(사용자 결정 A) — 월간 칩(칸 폭 약 128px)에 글자 배지를
 *     넣어 보니 배지가 칸을 다 차지해 일정 제목이 «아예» 안 보였다. 그래서 칩·시간 블록만 아이콘으로 둔다.
 *   'small' = 목록 줄(선택일 레일 · 일간 표)의 작은 배지(`size="sm"`), 'text' = 상세 제목의 기본 크기.
 *   ⚠ 「다가올 일정」은 캘린더 칸과 «같은» 'icon' 이다(2026-10-27 사용자 지시 — 칸과 같은 표시로 맞춘다).
 *   ⇒ 바깥 `span.resv-tag` 는 «자리 잡기·title·읽어 주는 이름»만 진다(공용 Badge 는 그 속성들을 안 받는다).
 *   ⚠ `hasChipTooltip` 규칙(칩 툴팁이 있는 곳은 title 을 뺀다)과 aria-label 은 «그대로»다.
 */
export function ReservationBadge({ event, variant = 'icon', hasChipTooltip = false }: {
  event: Pick<CalendarEventDto, 'reservations'>;
  variant?: 'icon' | 'small' | 'text';
  /**
   * 이 배지가 «스타일 툴팁이 붙은 칩» 안에 있나 (부르는 쪽의 사실).
   * true 면 제 `title` 을 달지 않는다 — 달면 칩 툴팁과 «둘 다» 뜬다.
   * ⇒ 그때 방 이름은 칩 툴팁이 말한다(`eventTooltip.tsx` 의 예약 줄).
   */
  hasChipTooltip?: boolean;
}) {
  useSyncExternalStore(subscribeCalendarLang, getCalendarLang, getCalendarLang);
  const pending = pendingReservations(event);
  if (pending.length === 0) return null;
  const names = pendingResourceNames(event);
  const label = tCalendar('calendar.room.badge_pending');
  if (variant === 'icon') {
    const title = names ? `${label} · ${names}` : label;
    return (
      <span className="resv-tag resv-tag--icon" {...(hasChipTooltip ? {} : { title })} aria-label={label} role="img">
        <AlertTriangle size={11} aria-hidden />
      </span>
    );
  }
  // 'small'(목록 줄)의 title 은 종전 아이콘 시절 문구 그대로(「회의실 승인 필요 · 방」) — 그 자리에서 쓰던 말을 바꾸지 않는다.
  const title = variant === 'small' ? (names ? `${label} · ${names}` : label) : (names || undefined);
  return (
    <span className="resv-tag" title={title}>
      <Badge variant="warning" size={variant === 'small' ? 'sm' : 'md'}>{label}</Badge>
    </span>
  );
}
